import { isEmpty } from 'lodash';

import { GetAccountResponse } from 'apps-common/graphql/getAccount';
import { AccessUserTypes, MembershipType } from 'apps-common/types';
import { isStrictNever } from 'apps-common/utils/isStrictNever';
import { isActiveSubscription, isLegacyMember, isPendingSubscription } from 'apps-common/utils/member';

export const getUserType = (
  membershipType: MembershipType,
  currentSubscription: GetAccountResponse['account']['currentSubscription'] | undefined,
  paymentMethods: GetAccountResponse['account']['paymentMethods'] | undefined,
  isValidAddress: boolean,
  isValidCurrency: boolean,
): AccessUserTypes => {
  if (isLegacyMember(membershipType)) {
    return AccessUserTypes.LEGACY;
  }

  if (!currentSubscription) {
    return AccessUserTypes.UNFINISHED;
  }

  const subscriptionState = currentSubscription.subscriptionState;

  switch (membershipType) {
    case MembershipType.LIFETIME: {
      return AccessUserTypes.LIFETIME;
    }

    case MembershipType.UNFINISHED: {
      return AccessUserTypes.UNFINISHED;
    }

    case MembershipType.UNVERIFIED: {
      return AccessUserTypes.UNVERIFIED;
    }

    case MembershipType.B2C: {
      if (isPendingSubscription(subscriptionState)) {
        return AccessUserTypes.PENDING_B2C;
      }

      // Active subscription
      if (isActiveSubscription(subscriptionState) && !currentSubscription.pendingCancellation) {
        if (!isValidCurrency) {
          return AccessUserTypes.INVALID_CURRENCY_ACTIVE_B2C;
        }
        if (!isEmpty(paymentMethods)) {
          return isValidAddress
            ? AccessUserTypes.ACTIVE_B2C_ADDRESS_PAYMENT_METHOD
            : AccessUserTypes.ACTIVE_B2C_NO_ADDRESS_PAYMENT_METHOD;
        }
        return isValidAddress
          ? AccessUserTypes.ACTIVE_B2C_ADDRESS_NO_PAYMENT_METHOD
          : AccessUserTypes.ACTIVE_B2C_NO_ADDRESS_NO_PAYMENT_METHOD;
      }

      if (currentSubscription.pendingCancellation && !isValidCurrency) {
        return AccessUserTypes.INVALID_CURRENCY_ACTIVE_B2C;
      }

      // Expired or pending cancellation subscription
      if (!isEmpty(paymentMethods)) {
        return isValidAddress
          ? AccessUserTypes.INACTIVE_B2C_ADDRESS_PAYMENT_METHOD
          : AccessUserTypes.INACTIVE_B2C_NO_ADDRESS;
      }
      return isValidAddress
        ? AccessUserTypes.INACTIVE_B2C_ADDRESS_NO_PAYMENT_METHOD
        : AccessUserTypes.INACTIVE_B2C_NO_ADDRESS_NO_PAYMENT_METHOD;
    }

    case MembershipType.B2B: {
      if (isActiveSubscription(subscriptionState)) {
        return AccessUserTypes.ACTIVE_B2B;
      }
      return AccessUserTypes.INACTIVE_B2B;
    }

    default: {
      return isStrictNever(membershipType);
    }
  }
};
